import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Grid,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert
} from '@mui/material';
import { LocalHospital, MedicalServices } from '@mui/icons-material';

const AddTreatment = ({ open, onClose, onAdd }) => {
  const [formData, setFormData] = useState({
    treatment_type: '',
    medicine_name: '',
    dosage: '',
    date: '',
    cost: '',
    notes: ''
  });
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    // Basic validation
    if (!formData.treatment_type || !formData.date) {
      setError('Please fill in all required fields');
      return;
    }

    // Create new treatment object
    const newTreatment = {
      id: Date.now(), // Simple ID generation for demo
      treatment_type: formData.treatment_type,
      medicine_name: formData.medicine_name,
      dosage: formData.dosage,
      date: formData.date,
      cost: parseFloat(formData.cost) || 0,
      notes: formData.notes,
      created_at: new Date().toISOString()
    };

    onAdd(newTreatment);
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      treatment_type: '',
      medicine_name: '',
      dosage: '',
      date: '',
      cost: '',
      notes: ''
    });
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalHospital color="primary" />
          <Typography variant="h6">Add Treatment Record</Typography>
        </Box>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid size={12}>
              <FormControl fullWidth required>
                <InputLabel>Treatment Type *</InputLabel>
                <Select
                  name="treatment_type"
                  value={formData.treatment_type}
                  onChange={handleChange}
                  label="Treatment Type *"
                >
                  <MenuItem value="vaccination">Vaccination</MenuItem>
                  <MenuItem value="medication">Medication</MenuItem>
                  <MenuItem value="health_check">Health Check</MenuItem>
                  <MenuItem value="deworming">Deworming</MenuItem>
                  <MenuItem value="vitamin_supplement">Vitamin Supplement</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={12}>
              <TextField
                name="medicine_name"
                label="Medicine/Vaccine Name"
                value={formData.medicine_name}
                onChange={handleChange}
                fullWidth
                placeholder="e.g., Newcastle Disease Vaccine, Amprolium"
              />
            </Grid>

            <Grid size={12}>
              <TextField
                name="dosage"
                label="Dosage"
                value={formData.dosage}
                onChange={handleChange}
                fullWidth
                placeholder="e.g., 1ml per bird, 10mg/kg"
              />
            </Grid>

            <Grid size={12}>
              <TextField
                name="date"
                label="Date *"
                type="date"
                value={formData.date}
                onChange={handleChange}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={12}>
              <TextField
                name="cost"
                label="Cost (optional)"
                type="number"
                value={formData.cost}
                onChange={handleChange}
                fullWidth
                inputProps={{ min: 0, step: 0.01 }}
                placeholder="Treatment cost"
              />
            </Grid>

            <Grid size={12}>
              <TextField
                name="notes"
                label="Notes"
                value={formData.notes}
                onChange={handleChange}
                fullWidth
                multiline
                rows={3}
                placeholder="Additional notes about the treatment..."
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary">
            Add Treatment
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddTreatment;
